import Typography from '@mui/material/Typography';
import ResponsiveImage from '@/components/Image/ResponsiveImage';
import useStyles from './CheckoutOrderSummary.styles';
import type { OrderLine, OrderSummary } from './checkoutOrderSummaryData';
import { getRasterImage } from './subscriptionOffer';

interface PriceRowProps extends Pick<OrderLine, 'price'> {
  label: string;
  isTotal?: boolean;
}

function PriceRow({ label, price, isTotal = false }: PriceRowProps) {
  const { classes } = useStyles();
  const variant = isTotal ? 'h4' : 'bodyMd';

  return (
    <div className={classes.priceRow}>
      <Typography variant={variant} component='span'>
        {label}
      </Typography>
      <Typography variant={variant} component='span' className={classes.price}>
        {price}
      </Typography>
    </div>
  );
}

interface Props {
  orderSummary: OrderSummary;
}

export default function CheckoutOrderSummary({
  orderSummary: { products, monthly, annual },
}: Props) {
  const { classes } = useStyles();

  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <Typography variant='h3' component='h2'>
          Your products
        </Typography>
        <ul className={classes.products}>
          {products.map(({ id, name, price, image }) => {
            const frontifyImage = getRasterImage(image);

            return (
              <li key={id} className={classes.product}>
                <div className={classes.imageContainer}>
                  {frontifyImage && (
                    <ResponsiveImage
                      src={frontifyImage}
                      alt={frontifyImage.title ?? ''}
                      sizes={{ xs: 150 }}
                      className={classes.image}
                    />
                  )}
                </div>
                <div className={classes.productContent}>
                  <Typography variant='bodyMd' component='span'>
                    {name}
                  </Typography>
                  <Typography
                    variant='bodyMd'
                    component='span'
                    className={classes.price}
                  >
                    {price}
                  </Typography>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
      <section className={classes.section}>
        <Typography variant='h3' component='h2'>
          Total breakdown
        </Typography>
        {[
          monthly && { id: 'monthly', label: 'Monthly cost', ...monthly },
          { id: 'annual', label: 'Annual cost', ...annual },
        ]
          .filter((breakdown) => breakdown !== undefined)
          .map(({ id, label, lines, total }) => (
            <div key={id} className={classes.breakdown}>
              {lines.map((line) => (
                <PriceRow key={line.id} label={line.name} price={line.price} />
              ))}
              <PriceRow label={label} price={total} isTotal />
            </div>
          ))}
      </section>
    </div>
  );
}
